import { createDemo } from '@mui/internal-core-docs/utils/createDemo';
import LinkRouterWithTheme from './LinkRouterWithTheme';
import ClientProvider from './client';
export default createDemo(import.meta.url, LinkRouterWithTheme, { ClientProvider, precompute: {
  "Default": {
    "url": "file:///opt/build/repo/docs/data/material/integrations/routing/demos/link-router-with-theme/LinkRouterWithTheme.tsx",
    "fileName": "LinkRouterWithTheme.tsx",
    "language": "tsx",
    "source": {
      "type": "root",
      "children": [
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "import * as React from 'react';\nimport { Link as RouterLink, MemoryRouter, StaticRouter } from 'react-router';\nimport type { LinkProps as RouterLinkProps } from 'react-router';\nimport { ThemeProvider, createTheme } from '@mui/material/styles';\nimport Button from '@mui/material/Button';\nimport Stack from '@mui/material/Stack';\nimport Link, { LinkProps } from '@mui/material/Link';\n\nconst LinkBehavior = React.forwardRef<\n  HTMLAnchorElement,\n  Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }\n>((props, ref) => {\n  const { href, ...other } = props;\n  // Map href (MUI) -> to (react-router)\n  return <RouterLink data-testid=\"custom-link\" ref={ref} to={href} {...other} />;\n});\n\nfunction Router(props: { children?: React.ReactNode }) {\n  const { children } = props;\n  if (typeof window === 'undefined') {\n    return <StaticRouter location=\"/\">{children}</StaticRouter>;\n  }\n\n  return <MemoryRouter>{children}</MemoryRouter>;\n}\n\nconst theme = createTheme({\n  components: {\n    MuiLink: {\n      defaultProps: {\n        component: LinkBehavior,\n      } as LinkProps,\n    },\n    MuiButtonBase: {\n      defaultProps: {\n        LinkComponent: LinkBehavior,\n      },\n    },\n  },\n});\n\nexport default function LinkRouterWithTheme() {\n  return (\n    <Stack spacing={1} sx={{ alignItems: 'center', typography: 'body1' }}>\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "dataFrameType": "focus",
            "dataFrameIndent": 3,
            "dataFrameFocusTarget": "",
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "      <ThemeProvider theme={theme}>\n        <Router>\n          <Link href=\"/\">Link</Link>\n          <Button href=\"/\" variant=\"contained\">\n            Link\n          </Button>\n        </Router>\n      </ThemeProvider>\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "    </Stack>\n  );\n}\n"
            }
          ]
        }
      ],
      "data": {
        "totalLines": 55,
        "focusedLines": 8,
        "collapsible": true
      }
    },
    "fallback": [
      [
        "span",
        "frame",
        "import * as React from 'react';\nimport { Link as RouterLink, MemoryRouter, StaticRouter } from 'react-router';\nimport type { LinkProps as RouterLinkProps } from 'react-router';\nimport { ThemeProvider, createTheme } from '@mui/material/styles';\nimport Button from '@mui/material/Button';\nimport Stack from '@mui/material/Stack';\nimport Link, { LinkProps } from '@mui/material/Link';\n\nconst LinkBehavior = React.forwardRef<\n  HTMLAnchorElement,\n  Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }\n>((props, ref) => {\n  const { href, ...other } = props;\n  // Map href (MUI) -> to (react-router)\n  return <RouterLink data-testid=\"custom-link\" ref={ref} to={href} {...other} />;\n});\n\nfunction Router(props: { children?: React.ReactNode }) {\n  const { children } = props;\n  if (typeof window === 'undefined') {\n    return <StaticRouter location=\"/\">{children}</StaticRouter>;\n  }\n\n  return <MemoryRouter>{children}</MemoryRouter>;\n}\n\nconst theme = createTheme({\n  components: {\n    MuiLink: {\n      defaultProps: {\n        component: LinkBehavior,\n      } as LinkProps,\n    },\n    MuiButtonBase: {\n      defaultProps: {\n        LinkComponent: LinkBehavior,\n      },\n    },\n  },\n});\n\nexport default function LinkRouterWithTheme() {\n  return (\n    <Stack spacing={1} sx={{ alignItems: 'center', typography: 'body1' }}>\n"
      ],
      [
        "span",
        "frame",
        {
          "dataFrameType": "focus",
          "dataFrameIndent": 3,
          "dataFrameFocusTarget": ""
        },
        "      <ThemeProvider theme={theme}>\n        <Router>\n          <Link href=\"/\">Link</Link>\n          <Button href=\"/\" variant=\"contained\">\n            Link\n          </Button>\n        </Router>\n      </ThemeProvider>\n"
      ],
      [
        "span",
        "frame",
        "    </Stack>\n  );\n}\n"
      ]
    ],
    "fallbackCritical": {
      "1": [
        "span",
        "frame",
        {
          "dataFrameType": "focus",
          "dataFrameIndent": 3,
          "dataFrameFocusTarget": ""
        },
        [
          [
            "span",
            "line",
            {
              "dataLn": 45
            },
            [
              "      <",
              [
                "span",
                "pl-c1 di-jsx",
                "ThemeProvider"
              ],
              " ",
              [
                "span",
                "pl-e di-ak",
                "theme"
              ],
              [
                "span",
                "pl-k di-pu di-ae",
                "="
              ],
              [
                "span",
                "pl-pse",
                "{"
              ],
              [
                "span",
                "pl-smi di-jv",
                "theme"
              ],
              [
                "span",
                "pl-pse",
                "}"
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 46
            },
            [
              "        <",
              [
                "span",
                "pl-c1 di-jsx",
                "Router"
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 47
            },
            [
              "          <",
              [
                "span",
                "pl-c1 di-jsx",
                "Link"
              ],
              " ",
              [
                "span",
                "pl-e di-ak",
                "href"
              ],
              [
                "span",
                "pl-k di-pu di-ae",
                "="
              ],
              [
                "span",
                "pl-s di-av",
                [
                  [
                    "span",
                    "pl-pds",
                    "\""
                  ],
                  "/",
                  [
                    "span",
                    "pl-pds",
                    "\""
                  ]
                ]
              ],
              ">Link</",
              [
                "span",
                "pl-c1 di-jsx",
                "Link"
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 48
            },
            [
              "          <",
              [
                "span",
                "pl-c1 di-jsx",
                "Button"
              ],
              " ",
              [
                "span",
                "pl-e di-ak",
                "href"
              ],
              [
                "span",
                "pl-k di-pu di-ae",
                "="
              ],
              [
                "span",
                "pl-s di-av",
                [
                  [
                    "span",
                    "pl-pds",
                    "\""
                  ],
                  "/",
                  [
                    "span",
                    "pl-pds",
                    "\""
                  ]
                ]
              ],
              " ",
              [
                "span",
                "pl-e di-ak",
                "variant"
              ],
              [
                "span",
                "pl-k di-pu di-ae",
                "="
              ],
              [
                "span",
                "pl-s di-av",
                [
                  [
                    "span",
                    "pl-pds",
                    "\""
                  ],
                  "contained",
                  [
                    "span",
                    "pl-pds",
                    "\""
                  ]
                ]
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 49
            },
            "            Link"
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 50
            },
            [
              "          </",
              [
                "span",
                "pl-c1 di-jsx",
                "Button"
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 51
            },
            [
              "        </",
              [
                "span",
                "pl-c1 di-jsx",
                "Router"
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 52
            },
            [
              "      </",
              [
                "span",
                "pl-c1 di-jsx",
                "ThemeProvider"
              ],
              ">"
            ]
          ],
          "\n"
        ]
      ]
    },
    "totalLines": 55,
    "focusedLines": 8,
    "collapsible": true,
    "externals": [
      "react",
      "react-router",
      "@mui/material/styles",
      "@mui/material/Button",
      "@mui/material/Stack",
      "@mui/material/Link"
    ]
  }
}, loadPrecompute: () => import("./index.ts?mui-deferred-precompute").then((module) => module.default) });
